// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import {
  Button,
  Column,
  Description,
  InlineAlert,
  InputText,
  LgSpacing,
  MdSpacing,
  Row,
  Toast,
  ToastContext,
  XlSpacing,
  XxlSpacing,
  XxsSpacing,
  useScreenSize,
} from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type * as stripeJs from '@stripe/stripe-js';
import React, { useContext, useState } from 'react';
import { type PaymentMethodType } from '~shared/constants';
import { getErrorMessage } from '~shared/helpers/helper';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useCreateCreditCardPaymentMethod } from '~shared/queries/usePaymentMethodMutations';
import { type APIError } from '~shared/types/APIError';
import { stripePromise } from '../helpers/stripePromise.web';
import { AddPaymentCardPaymentElement } from './AddPaymentCardPaymentElement.web';
import { StripeInputLabel } from './StripeInputLabel';
import { StripeInputText } from './StripeInputText';

type Props = {
  readonly onClose: () => void;
  // pass payment method type when in checkout context
  // otherwise leave undefined so payment method is not updated for order
  readonly paymentMethodType?: PaymentMethodType;
};

type AddPaymentCardProps = Props & {
  // Render the legacy card form directly - used when a host already tried the
  // new form and is falling back.
  readonly forceLegacy?: boolean;
};

export const stripeBaseStyle = {
  base: {
    backgroundColor: COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTEST,
    color: COLORS.TEXT_COLORS.BLACK,
    lineHeight: '52px',
    '::placeholder': {
      color: COLORS.TEXT_COLORS.DISABLED,
    },
  },
  invalid: {
    color: COLORS.TEXT_COLORS.DANGER,
  },
};

const AddPaymentCardForm = ({ onClose, paymentMethodType }: Props) => {
  const { trackEvent } = useAnalytics();
  const { isMDScreenOrBigger } = useScreenSize();
  const [stripeFieldComplete, setStripeFieldComplete] = useState({ cardNumber: false, expDate: false, cvc: false });
  const [zipCode, setZipCode] = useState<string>('');
  const stripe = useStripe();
  const elements = useElements();
  const { addToast } = useContext(ToastContext);
  const { mutateAsync: createCreditCardPaymentMethod } = useCreateCreditCardPaymentMethod();
  // Covers the whole submit (Stripe tokenization + our POST), like the old
  // ui.loading.createPaymentMethodLoading flag the thunk set before tokenizing.
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const loading = submitting;

  const validZipCode = (() => {
    const validZip = zipCode.match(/\d/g);
    // eslint-disable-next-line sonarjs/prefer-single-boolean-return
    if (validZip && validZip.length === 5) {
      return true;
    }
    return false;
  })();

  const handleSubmit = async () => {
    if (loading || !stripe || !elements) {
      return;
    }
    trackEvent({ event: EVENTS.ADD_CARD_TAPPED });

    // Start the loading state before the async call to Stripe, both to show progress
    // during tokenization and to prevent duplicate form submissions.
    setSubmitting(true);
    setSubmitError(null);

    try {
      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        // this should never happen, but it's here to satisfy typescript
        throw new Error('Error setting up your card');
      }

      const tokenResult = await stripe.createToken(cardElement, { address_zip: zipCode });
      if (tokenResult.error) {
        // Tokenization errors are patient-fixable input problems (bad number/expiry/zip),
        // not create failures — show them without the failure analytics event.
        setSubmitError(tokenResult.error.message ?? null);
        return;
      }

      await createCreditCardPaymentMethod({
        // @ts-expect-error this should be fine
        token: tokenResult.token,
        paymentMethodType,
      });
      onClose();
      addToast(<Toast variant="success">Successfully added credit card!</Toast>);
      trackEvent({
        event: EVENTS.PAYMENT_METHOD_CREATED,
        params: {
          newStripeFormEnabled: false,
          type: 'StripeData',
        },
      });
    } catch (error) {
      setSubmitError(getErrorMessage(error as APIError) ?? null);
      trackEvent({
        event: EVENTS.CREATE_PAYMENT_METHOD_FAILED,
        params: {
          newStripeFormEnabled: false,
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const validateElement = ({ complete }: stripeJs.StripeElementChangeEvent, elementType: string) => {
    setStripeFieldComplete((prev) => ({ ...prev, [elementType]: complete }));
  };

  return (
    <>
      {isMDScreenOrBigger ? (
        <>
          <StripeInputText text="Card number">
            <CardNumberElement
              onChange={(e) => {
                validateElement(e, 'cardNumber');
              }}
              options={{
                showIcon: true,
                style: stripeBaseStyle,
              }}
            />
          </StripeInputText>
          <XlSpacing />
          <Row>
            <Column flexGrow={1}>
              <StripeInputText text="Exp date">
                <CardExpiryElement
                  onChange={(e) => {
                    validateElement(e, 'expDate');
                  }}
                  options={{
                    style: stripeBaseStyle,
                  }}
                />
              </StripeInputText>
            </Column>
            <MdSpacing />
            <Column flexGrow={1}>
              <StripeInputText text="Security code">
                <CardCvcElement
                  onChange={(e) => {
                    validateElement(e, 'cvc');
                  }}
                  options={{
                    style: stripeBaseStyle,
                  }}
                />
              </StripeInputText>
            </Column>
            <MdSpacing />
            <Column flexGrow={1}>
              <StripeInputLabel text="Billing zip code" />
              <XxsSpacing />
              <InputText
                accessibilityLabel="Zip"
                autoCapitalize="none"
                autoComplete="postal-code"
                keyboardType="number-pad"
                maxLength={5}
                onChangeText={(value) => {
                  setZipCode(value);
                }}
                placeholder="Zip code"
                required
                textContentType="postalCode"
              />
            </Column>
          </Row>
        </>
      ) : (
        <>
          <StripeInputText text="Card number">
            <CardNumberElement
              onChange={(e) => {
                validateElement(e, 'cardNumber');
              }}
              options={{
                showIcon: true,
                style: stripeBaseStyle,
              }}
            />
          </StripeInputText>
          <MdSpacing />
          <Row>
            <Column flexGrow={1}>
              <StripeInputText text="Exp date">
                <CardExpiryElement
                  onChange={(e) => {
                    validateElement(e, 'expDate');
                  }}
                  options={{
                    style: stripeBaseStyle,
                  }}
                />
              </StripeInputText>
            </Column>
            <MdSpacing />
            <Column flexGrow={1}>
              <StripeInputText text="Security code">
                <CardCvcElement
                  onChange={(e) => {
                    validateElement(e, 'cvc');
                  }}
                  options={{
                    style: stripeBaseStyle,
                  }}
                />
              </StripeInputText>
            </Column>
          </Row>
          <MdSpacing />
          <StripeInputLabel text="Billing zip code" />
          <XxsSpacing />
          <InputText
            accessibilityLabel="Zip"
            autoCapitalize="none"
            autoComplete="postal-code"
            keyboardType="number-pad"
            maxLength={5}
            onChangeText={(value) => {
              setZipCode(value);
            }}
            placeholder="Zip code"
            required
            textContentType="postalCode"
          />
        </>
      )}
      {submitError ? (
        <>
          <LgSpacing />
          <InlineAlert type="error">
            <Description>{submitError}</Description>
          </InlineAlert>
        </>
      ) : null}
      <XxlSpacing />
      <Button
        key="add-card-submit"
        disabled={loading || Object.values(stripeFieldComplete).includes(false) || !validZipCode}
        label="Add card"
        loading={loading}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onPress={handleSubmit}
      />
      <MdSpacing />
      <Button
        key="add-card-cancel-button"
        disabled={loading}
        label="Cancel"
        onPress={onClose}
        type="tertiary"
      />
    </>
  );
};

export const AddPaymentCard = ({ onClose, paymentMethodType, forceLegacy }: AddPaymentCardProps) => {
  const { value: paymentElementEnabled } = Experimentation.useFeatureFlag('stripe_payment_element_web');
  // A SetupIntent failure on the new path shouldn't block adding a card - fall back
  // to the legacy form, which renders immediately and only needs Stripe at submit.
  const [stripeUnavailable, setStripeUnavailable] = useState(false);

  if (paymentElementEnabled && !stripeUnavailable && !forceLegacy) {
    return (
      <AddPaymentCardPaymentElement
        onClose={onClose}
        onStripeUnavailable={() => {
          setStripeUnavailable(true);
        }}
        paymentMethodType={paymentMethodType}
      />
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <AddPaymentCardForm
        onClose={onClose}
        paymentMethodType={paymentMethodType}
      />
    </Elements>
  );
};
